/* Motion kit - reduced-motion-first, one shared exponential ease-out, calm.
   Motion conveys state (route reveal, KPI count-up), never decoration. Every
   export degrades to instant when the user prefers reduced motion. */
import { motion, useReducedMotion } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";

// arrive fast, settle soft, no bounce
export const EASE = [0.2, 0, 0, 1] as const;

/** Route-level content reveal: a calm rise + fade on each navigation.
    `routeKey` is the top-level path SEGMENT (not the full pathname), so a
    param-only change within one section (e.g. /runs/123 -> /runs/456) does NOT
    remount the subtree - React reconciles it in place, preserving page-local
    state and avoiding a skeleton reflash. Crossing sections remounts and reveals.
    The wrapper transform is cleared to `none` on completion so a lingering
    transform can't establish a containing block for the app's inline
    position:fixed modals. Reduced-motion -> quick fade, no movement. */
export function PageReveal({ routeKey, children }: { routeKey: string; children: ReactNode }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  return (
    <motion.div
      key={routeKey}
      ref={ref}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0.14 : 0.34, ease: EASE }}
      onAnimationComplete={() => {
        if (ref.current) ref.current.style.transform = "none";
      }}
    >
      {children}
    </motion.div>
  );
}

/** Count-up for KPI/hero numerals. Animates ONCE when it first appears (from the
    currently displayed value), then shows live values instantly - so a
    react-query-polled counter does not perpetually re-animate or lag. Integer
    targets are rounded so formatters that don't round below their K/KB threshold
    never flash a fractional intermediate. Reduced-motion shows the value at once. */
export function AnimatedNumber({
  value,
  format,
  durationMs = 500,
}: {
  value: number;
  format?: (n: number) => string;
  durationMs?: number;
}) {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);
  const displayRef = useRef(display);
  displayRef.current = display;
  const doneRef = useRef(reduce);

  useEffect(() => {
    // after the one-time intro (or under reduced motion) snap to live values
    if (doneRef.current) {
      setDisplay(value);
      return;
    }
    const from = displayRef.current;
    const to = value;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else doneRef.current = true;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);

  const shown = doneRef.current ? value : display;
  const n = Number.isInteger(value) ? Math.round(shown) : shown;
  return <>{format ? format(n) : Math.round(n).toLocaleString()}</>;
}
